/**
 * Cliente API mínimo. credentials:'include' para que viaje la cookie de sesión
 * httpOnly. En dev, Vite proxya /api y /health al backend (mismo origen).
 */
import { avisarFalloDeCarga } from './fallos';

export class ApiError extends Error {
  status: number;
  code?: string; // el campo `error` del backend (ej. 'account_locked')
  retryAfterSec?: number; // en respuestas 429
  /**
   * true = este 401 YA se resolvió mandando a la persona al login (ver más abajo).
   *
   * Existe para que Sentry pueda distinguirlo. Una sesión que vence es lo más normal del mundo y
   * el app ya hace lo correcto —guarda dónde estaba y redirige—, pero el `throw` igual viaja: de
   * las 91 pantallas que llaman a su `load()` en un useEffect, ninguna lo ataja, así que termina
   * como `unhandledrejection` y llega a Sentry como si fuera un bug (2026-09-17, /pay-queue).
   *
   * No se puede simplemente no tirar: el llamador tiene que cortar su ejecución igual.
   */
  sessionExpired?: boolean;
  /** El JSON completo de la respuesta (para errores que traen datos, ej. company_required → companies). */
  body?: unknown;
  constructor(status: number, message: string, code?: string, retryAfterSec?: number, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.code = code;
    this.retryAfterSec = retryAfterSec;
  }
}

/**
 * FALLO DE RED (no del servidor): `fetch` rechaza con `TypeError: Failed to fetch` cuando el
 * backend se está reiniciando (cada deploy de Railway tarda ~20-40s en volver) o cuando al
 * usuario se le cae el internet — con 90% del equipo en móvil eso pasa seguido. Antes reventaba
 * como un TypeError crudo: el usuario veía "nada" o un "Error" pelado, y en Sentry entraba como
 * error de código. Ahora:
 *   - GET (idempotente): se reintenta solo, así una recarga durante un deploy simplemente funciona.
 *   - POST/PUT/PATCH/DELETE: NUNCA se reintenta solo (reintentar a ciegas es justo lo que crea
 *     duplicados, ej. los 3 "ATR request"); se devuelve un error tipado para avisar bien.
 */
export const NETWORK_ERROR = 'network_error';
// ~25 s en total. Antes eran 1,6 s (400 + 1200): un reinicio por deploy dura mas que eso, asi que
// el GET se rendia y la pantalla que lo pedia se quedaba sin datos (reporte de Viviana 2026-09-14:
// "todos los correos desaparecen"). Solo GET: los demas metodos siguen sin reintentarse.
const RETRY_DELAYS_MS = [400, 1200, 3000, 6000, 15000];
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * EL PROXY DE RAILWAY MIENTRA EL BACKEND ARRANCA. Durante el swap de un deploy no hay contenedor
 * sano: el edge responde 502 con "Application failed to respond" (y 503/504 si tarda). Eso NO es
 * un fallo de la petición, es la ventana de arranque — y hasta ahora sí se reintentaba el `fetch`
 * que rechaza en crudo pero NO estas respuestas, así que un GET moría al primer intento y la
 * pantalla quedaba rota. Medido en prod el 7 de agosto: tres pushes en 40 minutos (19:59, 20:19 y
 * 20:36 UTC) y dos usuarios distintos comiéndose la ventana (502s a las 20:33 en /claims/1960 y
 * fetch caído a las 20:50 en /claims). Solo GET: reintentar un POST a ciegas crea duplicados.
 */
const RETRY_STATUS = new Set([502, 503, 504]);

/**
 * Mensaje legible del fallo de red, en el idioma del usuario. Va en `message` para que TODOS los
 * `catch (e) { alert(e instanceof ApiError ? e.message : 'Error') }` que ya existen muestren algo
 * útil sin tocar cada pantalla. No promete "no se guardó nada": la petición pudo llegar al
 * servidor y perderse la respuesta, así que se le pide al usuario que verifique antes de repetir.
 */
function networkErrorMessage(idempotent: boolean): string {
  const es = (typeof localStorage !== 'undefined' && localStorage.getItem('lang')) === 'es';
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (es) {
    return offline
      ? 'Sin conexión a internet. Revisa tu señal e inténtalo otra vez.'
      : idempotent
        ? 'No se pudo conectar con el servidor (puede estar actualizándose). Espera unos segundos y recarga.'
        : 'Se perdió la conexión al enviar. Verifica si se guardó antes de intentarlo de nuevo.';
  }
  return offline
    ? 'No internet connection. Check your signal and try again.'
    : idempotent
      ? "Couldn't reach the server (it may be updating). Wait a few seconds and reload."
      : 'The connection dropped while sending. Check whether it saved before trying again.';
}

/**
 * ¿HAY ALGUIEN DEL OTRO LADO? — reporte de Manuel (2026-08-21): "Darwin, Christian, Jessika y
 * Paula se ve que están conectados siempre, ¿por qué?".
 *
 * Porque una pestaña abierta trabaja sola. El app se refresca con temporizadores —la presencia
 * cada 20 segundos, los mensajes cada 6, las alertas cada 30— y el backend contaba CUALQUIER
 * request autenticada como actividad de la persona. Medido en prod el 21-ago: los tres tenían las
 * 24 franjas de la madrugada marcadas y ~530 "actividades" en el día, de 00:00 a 09:00 sin un
 * hueco. Nadie trabaja así: era el navegador de anoche que quedó abierto.
 *
 * Eso no solo pinta un puntito verde de más. La misma señal alimenta la barra de productividad
 * (todo el día activo = 100% siempre, un número que ya no sirve para nadie), el fichaje
 * automático y cualquier regla de "lleva rato sin hacer nada".
 *
 * La marca viaja en CADA request: cuántos milisegundos hace que la persona tocó algo de verdad
 * (teclado, mouse, dedo). El servidor decide con eso; acá solo se mide. Los oyentes son pasivos y
 * lo único que hacen es guardar un número, así que no cuestan nada.
 */
let ultimaInteraccion = Date.now();

/**
 * PRENDER LA PANTALLA NO ES TRABAJAR — segunda vuelta (2026-08-23): "todavía veo a Paula y
 * Jessika conectadas".
 *
 * Seguían apareciendo, pero mucho menos: medido en prod el 23-ago, las dos tienen franjas
 * sueltas de madrugada (00:13, 01:05, 03:03, 06:02…) con **exactamente 1 hit cada una**, y
 * cero acciones reales — la última de Paula fue el viernes a las 16:17. La firma es inconfundible:
 * quien de verdad trabaja deja 3 a 5 hits por franja (Manuel, 4,6); un hit solo y aislado es una
 * máquina que se despertó un segundo.
 *
 * Qué la despertaba: esta misma línea. `visibilitychange` marcaba interacción con solo volverse
 * visible la pestaña, y una pantalla se prende sola toda la noche —el teléfono con una
 * notificación, la laptop en sus despertares de mantenimiento—. Un segundo de pantalla y la
 * persona figuraba activa; peor, se reseteaba el reloj de inactividad, así que el cierre de sesión
 * a las 4 horas NUNCA llegaba a dispararse: la sesión se renovaba sola hasta el infinito.
 *
 * La regla ahora: volver DESPUÉS DE UN RATO CORTO sigue contando (fuiste al correo y volviste,
 * estás trabajando); volver después de horas escondida no cuenta hasta que la persona toque algo
 * de verdad. Un despertar de madrugada cae siempre del lado que no cuenta.
 */
const GRACIA_AL_VOLVER_MS = 5 * 60_000;

/**
 * LA ACTIVIDAD ES DEL NAVEGADOR, NO DE LA PESTAÑA — reporte de usuarios (2026-08-24): "tienen
 * varias pantallas abiertas con el CRM y están trabajando en una, pero se les desloguea de las
 * otras que no usan".
 *
 * Tenían razón y el efecto era peor de lo que parece: `ultimaInteraccion` vivía en la memoria de
 * CADA pestaña, así que la que quedaba de fondo seguía contando su propio ocio y a las dos horas
 * mandaba "nadie tocó nada". El servidor le creía y borraba la cookie… que es UNA sola para todo
 * el navegador. O sea: la pestaña dormida deslogueaba las que sí se estaban usando, y la persona
 * perdía la sesión en medio del trabajo.
 *
 * `localStorage` es compartido por todas las pestañas del mismo origen, así que sirve de pizarra
 * común: cada pestaña anota ahí cuándo hubo interacción y todas leen la MÁS RECIENTE. No hace
 * falta escuchar eventos entre pestañas — se lee al momento de armar la request, que es cuando
 * importa.
 *
 * Se escribe como mucho una vez cada 10 segundos: los eventos de scroll y wheel llegan de a
 * decenas por segundo y escribir en localStorage en cada uno bloquea el hilo de la pantalla.
 */
const LS_ULTIMA_INTERACCION = 'ppag:lastInput';
const GUARDAR_CADA_MS = 10_000;

/** La última interacción de CUALQUIER pestaña de este navegador. */
function ultimaInteraccionGlobal(): number {
  let compartida = 0;
  try {
    const v = Number(localStorage.getItem(LS_ULTIMA_INTERACCION));
    // Una marca del futuro (reloj cambiado, otro origen) se ignora en vez de dar ocio negativo.
    if (Number.isFinite(v) && v <= Date.now()) compartida = v;
  } catch { /* modo privado o storage lleno: queda la de esta pestaña */ }
  return Math.max(ultimaInteraccion, compartida);
}

if (typeof window !== 'undefined') {
  let ultimoGuardado = 0;
  const marcar = () => {
    ultimaInteraccion = Date.now();
    if (ultimaInteraccion - ultimoGuardado < GUARDAR_CADA_MS) return;
    ultimoGuardado = ultimaInteraccion;
    try { localStorage.setItem(LS_ULTIMA_INTERACCION, String(ultimaInteraccion)); } catch { /* ver arriba */ }
  };
  for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const) {
    window.addEventListener(ev, marcar, { passive: true, capture: true });
  }
  let ocultaDesde: number | null = null;
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { ocultaDesde = Date.now(); return; }
    const escondidaMs = ocultaDesde === null ? 0 : Date.now() - ocultaDesde;
    ocultaDesde = null;
    if (escondidaMs <= GRACIA_AL_VOLVER_MS) marcar();
  });
}

/**
 * Cabeceras de toda request: la marca de la última interacción humana y LA HORA LOCAL de quien
 * está del otro lado.
 *
 * La hora hace falta porque la tolerancia a la inactividad depende de si es horario laboral (2
 * horas) o no (30 minutos), y la empresa trabaja desde cinco husos: con la hora del servidor, la
 * jornada de quien está en Filipinas caería entera en "fuera de horario". El servidor la usa solo
 * para elegir el umbral y se cae a la hora de la oficina si no llega.
 */
function cabeceras(body?: unknown): Record<string, string> | undefined {
  const ahora = new Date();
  const hhmm = `${String(ahora.getHours()).padStart(2, '0')}:${String(ahora.getMinutes()).padStart(2, '0')}`;
  const h: Record<string, string> = {
    // La de CUALQUIER pestaña: trabajar en una mantiene viva la sesión de todas.
    'x-idle-ms': String(Date.now() - ultimaInteraccionGlobal()),
    'x-local-time': hhmm,
  };
  if (body) h['Content-Type'] = 'application/json';
  return h;
}

/**
 * `keepalive`: para el POST que sale en `pagehide` (la pestaña se está cerrando). Sin él, Chromium
 * cancela las requests iniciadas durante el unload y el aviso casi nunca llega: el otro lado se
 * queda con el video congelado hasta que el barrido lo cierra. El cuerpo es de pocos bytes (el
 * límite de keepalive son 64 KB) y las cabeceras `x-idle-ms`/`x-local-time` viajan igual.
 */
export type ReqOpts = { keepalive?: boolean };

async function req<T>(method: string, path: string, body?: unknown, opts?: ReqOpts): Promise<T> {
  const idempotent = method === 'GET';
  let res: Response;
  for (let attempt = 0; ; attempt++) {
    try {
      res = await fetch(path, {
        method,
        headers: cabeceras(body),
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include',
        keepalive: opts?.keepalive === true,
      });
      // El backend todavía no está arriba (deploy): se espera y se reintenta, igual que un
      // fetch que rechaza. Con los reintentos de siempre, un deploy deja de verse.
      if (idempotent && RETRY_STATUS.has(res.status) && attempt < RETRY_DELAYS_MS.length) {
        await sleep(RETRY_DELAYS_MS[attempt]!);
        continue;
      }
      break;
    } catch {
      // Solo los GET se reintentan; el resto se reporta al toque para que el usuario decida.
      if (idempotent && attempt < RETRY_DELAYS_MS.length) {
        await sleep(RETRY_DELAYS_MS[attempt]!);
        continue;
      }
      throw new ApiError(0, networkErrorMessage(idempotent), NETWORK_ERROR);
    }
  }
  const text = await res.text();
  // NO SIEMPRE VUELVE JSON. Cuando Railway está reiniciando o el proxy corta, el cuerpo es texto
  // plano ("upstream error") o HTML: `JSON.parse` reventaba con un SyntaxError crudo que llegaba
  // al ErrorBoundary y le tumbaba la pantalla al usuario (Sentry, 2026-07-27, /claims/1407).
  // Ahora un cuerpo que no es JSON se trata como lo que es: una respuesta rota del servidor.
  let data: { message?: string; error?: string; retryAfterSec?: number } | null = null;
  if (text) {
    try { data = JSON.parse(text); }
    catch {
      if (res.ok) throw new ApiError(res.status, networkErrorMessage(idempotent), NETWORK_ERROR);
      data = null; // error del server sin JSON → abajo cae al `http_<status>` de siempre
    }
  }
  if (!res.ok) {
    // SESIÓN EXPIRADA (401 fuera de /api/auth/*): en vez de reventar cada clic con un alert
    // "unauthorized" (reporte de Scarlett), recuerda dónde estaba el usuario y mándalo al
    // login. Al volver a entrar, Login lo regresa a la misma página.
    let sesionVencida = false;
    if (res.status === 401 && !path.startsWith('/api/auth/')) {
      const here = window.location.pathname;
      if (!here.includes('/login')) {
        try { sessionStorage.setItem('postLoginRedirect', here + window.location.search); } catch { /* */ }
        window.location.assign(here.startsWith('/platform') ? '/platform/login' : '/login');
        sesionVencida = true;
      }
    }
    const err = new ApiError(
      res.status,
      data?.message ?? data?.error ?? `http_${res.status}`,
      data?.error,
      data?.retryAfterSec,
      data,
    );
    // Se marca DESPUÉS de haber redirigido: solo cuenta como "ya resuelto" si de verdad se mandó
    // a la persona al login. Un 401 en la propia pantalla de login no lleva la marca.
    if (sesionVencida) err.sessionExpired = true;
    throw err;
  }
  return data as T;
}

/**
 * QUÉ CUENTA COMO "NO CARGÓ" y qué no. Solo un fallo de verdad: red caída, 5xx, 408, 429. Un 403 o
 * un 404 son el servidor respondiendo con coherencia ("no tenés permiso", "no existe") y hay cajas
 * que los esperan a propósito (ContractorAuthBox: "403 esperado para quien no es manager"):
 * avisarlos sería gritar por algo que ya está resuelto. Una sesión vencida tampoco es un fallo:
 * `api.ts` ya mandó a la persona al login y la pantalla se va a desmontar.
 */
export function esFalloDeVerdad(err: unknown): err is ApiError {
  if (!(err instanceof ApiError)) return false;
  if (err.sessionExpired) return false;
  if (err.code === NETWORK_ERROR || err.status === 0) return true;
  return err.status >= 500 || err.status === 408 || err.status === 429;
}

/**
 * GET EN VUELO COMPARTIDO (auditoría 2026-09-15): al abrir un claim, `claims/:id` se pedía tres
 * veces y `files` tres veces desde componentes hermanos que montan en el mismo tick. Dos GET
 * idénticos que salen mientras el primero todavía no respondió comparten UNA request; en cuanto
 * responde, el siguiente vuelve a ir a la red (no es caché: es dedupe de concurrencia).
 */
const enVuelo = new Map<string, Promise<unknown>>();
function getCompartido<T>(path: string): Promise<T> {
  const previa = enVuelo.get(path);
  if (previa) return previa as Promise<T>;
  const p = req<T>('GET', path)
    // NINGÚN GET VUELVE A FALLAR EN SILENCIO (2026-09-17). La barra global de fallos existía desde
    // la auditoría del 09-15, pero había que acordarse de llamarla: de los 94 `useEffect(() =>
    // load())` del front, la llamaban 9. Enganchada acá, donde pasan TODOS los GET, cubre también
    // los que se escriban mañana.
    .catch((e) => { if (esFalloDeVerdad(e)) avisarFalloDeCarga('datos', () => window.location.reload()); throw e; })
    .finally(() => { enVuelo.delete(path); });
  enVuelo.set(path, p);
  return p;
}

export const api = {
  get: <T>(path: string) => getCompartido<T>(path),
  post: <T>(path: string, body?: unknown, opts?: ReqOpts) => req<T>('POST', path, body, opts),
  put: <T>(path: string, body?: unknown) => req<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => req<T>('PATCH', path, body),
  del: <T>(path: string) => req<T>('DELETE', path),
};
